import { PureComponent, ReactNode } from 'react';
import { batch, connect, useDispatch, useSelector } from 'react-redux';
import { Table, Input } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import TableWithDragableRows, {
    DragHandle,
} from '../../../rightPanel/workspace/padV2/TableWithDragableRows';
import { EditableCell, EditableCellV2 } from '../../../rightPanel/workspace/padV2/MedTableRenderer';
import { buildOptions } from '../../../rightPanel/workspace/padV2/SymDiaTableV2';
import { track } from '../../../../../tracking';
import {
    copyDentalExaminationDate,
    deleteDentalExamination,
    swapPxElement,
    upsertDentalExamination,
} from '../../../../../store/actions/prescriptionPad';
import { PAD_V2_CONTAINER } from '../../../rightPanel/workspace/PadContainer';
import { columnHeaderContent, headerStyle } from '../../../../common/TableUtils';
import { EkaFocosableElement } from '../../../../common/types/EkaFocusable';
import {
    DENTAL_ENTITY_TYPE,
    DentalExamination,
    MedicationsEntity,
    Surface,
} from '../../../../../store/types/prescription';
import fetchMatchingSymDia, { SymDia } from '../../../../../api/fetchMatchingSymDia';
import { toPxDentalExaminationFromApiObj } from './DentalExaminationAutoSuggest';
import CopyToAll from '../../../rightPanel/workspace/padV2/CopyToAll';
import { DentalExaminationIcon, DentalProcedureIcon } from '../../../../common/Icons';
import AntSelectWithLabel from '../../../rightPanel/workspace/padV2/AntSelectWithLabel';
import { allSurfaces, teethToSurfacesArray } from '../constants';
import { getDentalExaminations } from '../../../../../store/selectors/prescriptionPad';
import { LOCAL_ID_PREFIX } from '../../../../../constants';
import { generateLocalIdForEntity, randomString } from '../../../../../store/utils';
import LabTestsDateSelector, {
    LAB_TESTS_FOLLOWUP_TYPE,
} from '../../../rightPanel/workspace/padV2/LabTestsDateSelector';
import {
    LAB_TESTS_FOLLOWUP_DATE_TYPE,
    LAB_TESTS_FOLLOWUP_TIME_TYPE,
} from '../../../../common/optionGeneratorUtils';
import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../../../../../store/reducers';
import { Actions } from '../../../../../store/actions';
import { DENTAL_EXAMINATION_AS_INPUT, Group } from '../DentalExamination';
import { ChartType } from '../DentalChart';
import { fetchMatchingProcedures } from '../../../../../api';
import PadGenieAutoSuggestFuseManager, {
    DENTAL_EXAMINATIONS_AND_DENTAL_PAST_PROCEDURES,
} from '../../../PadGenieAutoSuggestFuseManager';
const { Column } = Table;

type Props = {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    elements: ReadonlyArray<DentalExamination>;
    containerId: string;
    onElementSelect: (index?: number, option?: SymDia | DentalExamination) => void;
    onElementUpdate: (option: DentalExamination) => void;
    onElementDelete: (id: string, group?: Group) => void;
    onCopyDate: (id: string) => void;
    onDrag: ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => void;
    chartType: ChartType;
    group?: Group;
};

const labTestOnDefaultOption = [
    {
        label: 'Today',
        type: LAB_TESTS_FOLLOWUP_DATE_TYPE.DAY,
        value: randomString(8),
        timeType: LAB_TESTS_FOLLOWUP_TIME_TYPE.MID_DAY,
        timeValue: '0',
    },
    {
        label: 'Yesterday',
        type: LAB_TESTS_FOLLOWUP_DATE_TYPE.DAY,
        value: randomString(8),
        timeType: LAB_TESTS_FOLLOWUP_TIME_TYPE.YESTERDAY,
        timeValue: '0',
    },
    {
        label: 'Day Before Yesterday',
        type: LAB_TESTS_FOLLOWUP_DATE_TYPE.DAY,
        value: randomString(8),
        timeType: LAB_TESTS_FOLLOWUP_TIME_TYPE.DAY_BEFORE_YESTERDAY,
        timeValue: '0',
    },
];

class DentalExaminationTable extends PureComponent<Props> {
    static REF_MAP: EkaFocosableElement[][] = [];

    TABLE_ID = 'dental-examination-table';

    getStrValueAtIndex = (element: DentalExamination, index: number): string | undefined => {
        switch (index) {
            case 0:
                return element.id ? element.name : undefined;
            case 1:
                return element.teeth_id;
            case 2:
                return element.surfaces && element.surfaces?.map((i) => i.name).join(', ');
            case 3:
                return element.since;
            case 4:
                return element.remark;
        }
        return '';
    };

    findIndexForRecordKey = (key: string): number => {
        return this.props.elements.findIndex((e, index) => this.rowKeyFromId(e.id, index) === key);
    };

    elementSuggestApi = async (text: string): Promise<SymDia[]> => {
        try {
            const fetchExaminations = fetchMatchingSymDia(text, undefined, 'dental');
            const fetchProcedures = fetchMatchingProcedures(text, 'dental');
            const values = await Promise.all([fetchExaminations, fetchProcedures]);
            const options: SymDia[] = [];
            if (values[0]?.symptoms) {
                options.push(
                    ...values[0].symptoms.map((i) => ({
                        ...i,
                        type: DENTAL_ENTITY_TYPE.EXAMINATION,
                    })),
                );
            }
            if (values[0].diagnoses) {
                options.push(
                    ...values[0].diagnoses.map((i) => ({
                        ...i,
                        type: DENTAL_ENTITY_TYPE.EXAMINATION,
                    })),
                );
            }
            if (values[1]) {
                options.push(
                    ...values[1].map((i) => ({
                        ...i,
                        type: DENTAL_ENTITY_TYPE.PROCEDURE,
                    })),
                );
            }
            return options;
        } catch (e) {
            track('ERROR', {
                on: 'DentalExaminationTable#elementSuggestApi',
                error: (e as Error).message,
            });
            return [];
        }
    };

    createOption = (value: string) => {
        const id = `${LOCAL_ID_PREFIX}de-${new Date().getTime()}-${randomString(4)}`;
        return {
            id: id,
            name: value.trim(),
            entity_id: generateLocalIdForEntity(id),
            type: DENTAL_ENTITY_TYPE.EXAMINATION,
        };
    };

    handleSearch = async (value: string): Promise<SymDia[]> => {
        const { elements } = this.props;
        const fuse = PadGenieAutoSuggestFuseManager.getFuseForType(
            DENTAL_EXAMINATIONS_AND_DENTAL_PAST_PROCEDURES,
        );
        const gElements = PadGenieAutoSuggestFuseManager.getOriginalListForType(
            DENTAL_EXAMINATIONS_AND_DENTAL_PAST_PROCEDURES,
        );

        const all = await buildOptions<SymDia>(
            value,
            elements,
            gElements || [],
            this.elementSuggestApi,
            fuse,
            this.createOption,
        );

        return all;
    };

    nameHeaderContent = columnHeaderContent('Examination finding / Past procedure');
    surfaceHeaderContent = columnHeaderContent('Area');
    dateHeaderContent = columnHeaderContent('Since');
    notesHeaderContent = columnHeaderContent('Additional Notes');

    ExaminationOptionIcon = DentalExaminationIcon('w-12 h-12 fill-darwin-green-primary');
    ProcedureOptionIcon = DentalProcedureIcon('w-12 h-12 fill-darwin-accent-medicines-pink-800');

    renderCellAutosuggestItem = (option: DentalExamination) => {
        const isExamination = option.type === DENTAL_ENTITY_TYPE.EXAMINATION;
        return (
            <RenderCellAutosuggestItemJsx
                option={option}
                wrapperClassName={'mx-dropdown-item'}
                icon={isExamination ? this.ExaminationOptionIcon : this.ProcedureOptionIcon}
                iconWrapperClassName={isExamination ? 'circle-inventory-in-stock' : 'circle-mx'}
            />
        );
    };

    setPropertyInputRef = (ref: EkaFocosableElement, elIndex: number, propertyIndex: number) => {
        if (!DentalExaminationTableComponent.REF_MAP[elIndex]) {
            DentalExaminationTableComponent.REF_MAP[elIndex] = [];
        }

        DentalExaminationTableComponent.REF_MAP[elIndex][propertyIndex] = ref;
        return;
    };

    renderName = (_: DentalExamination, el: DentalExamination, index: number) => {
        const { elements, containerId, group } = this.props;

        return {
            children: (
                <div className="flex space-x-16 items-center">
                    {!group && <DragHandle />}
                    <div className="font-700 w-full text-neutral-black text-14 placeholder-neutral-6">
                        <EditableCell
                            fieldName={`de-${index}`}
                            label={'Examination or past procedure'}
                            setRef={(ref) => {
                                this.setPropertyInputRef(ref, index, 0);
                            }}
                            debounceDelay={200}
                            customDropdownClass={`py-8 mx-dropdown rounded-8`}
                            customDropdownWidth={350}
                            optionsRenderer={this.renderCellAutosuggestItem}
                            onSearch={this.handleSearch}
                            getLabelToCompare={(o) => o.name}
                            selectedValue={
                                elements[index] && elements[index].id ? elements[index] : undefined
                            }
                            onSelect={(option) => {
                                this.props.onElementSelect(el.index, option ? option : undefined);
                                setTimeout(() => {
                                    const t = document.getElementById(
                                        `${DENTAL_EXAMINATION_AS_INPUT}-${
                                            group?.id || el.teeth_id
                                        }`,
                                    ) as HTMLInputElement;
                                    t && t.focus();
                                }, 500);
                            }}
                            containerId={containerId}
                            placeholder={`Type examination or past procedure`}
                        />
                    </div>
                </div>
            ),
        };
    };

    renderSurface = (_: DentalExamination, el: DentalExamination) => {
        const { chartType } = this.props;
        return (
            <AntSelectWithLabel
                className="w-full"
                isMulti={true}
                showSearch={true}
                allowClear
                placeholder="Select area"
                value={el.surfaces}
                notFoundContent={null}
                options={teethToSurfacesArray(chartType)[el.teeth_id || ''] || allSurfaces}
                getValue={(o) => o?.id}
                getLabelToCompare={(o) => o.name}
                setValue={(t) => {
                    this.props.onElementUpdate({
                        ...el,
                        surfaces: t as Surface[],
                    });
                }}
                filterOption={true}
                optionFilterProp="name"
            />
        );
    };

    renderDate = (_: DentalExamination, el: DentalExamination, index: number) => {
        return {
            children: (
                <div>
                    <LabTestsDateSelector
                        defaultOptions={labTestOnDefaultOption}
                        date={el.since}
                        upsertElement={(dentalExamination) => {
                            this.props.onElementUpdate(dentalExamination as DentalExamination);
                        }}
                        elements={this.props.elements as MedicationsEntity[]}
                        el={{ ...el, medIndex: index }}
                        containerId={this.props.containerId}
                        type={LAB_TESTS_FOLLOWUP_TYPE.SINCE}
                        disableFutureDate
                    />

                    <CopyToAll
                        id={this.copyDateId(index)}
                        onClick={() => this.props.onCopyDate(el.entity_id || el.id)}
                        tableId={this.TABLE_ID}
                    />
                </div>
            ),
        };
    };

    renderNotes = (_: DentalExamination, el: DentalExamination, index: number) => {
        const value = el.remark || '';

        return {
            children: (
                <div className="flex space-x-8 items-center">
                    <EditableCellV2
                        defaultValue={value}
                        onBlur={(e) => {
                            this.props.onElementUpdate({ ...el, remark: e.target.value });
                        }}
                        setRef={(ref) => {
                            this.setPropertyInputRef(ref, index, 5);
                            return;
                        }}
                        label="Add notes here"
                    >
                        <Input.TextArea
                            autoFocus
                            defaultValue={value}
                            rows={1}
                            placeholder="Add notes here"
                        />
                    </EditableCellV2>
                </div>
            ),
        };
    };

    copyDateId = (index: number): string => `copy-date-v2-svg-${index}`;

    rowKeyFromId = (id: string, index?: number): string => `${'dental-examination'}-${index}`;

    render(): ReactNode {
        const { elements, onDrag, group } = this.props;
        if (elements.length === 0) {
            return null;
        }
        return (
            <TableWithDragableRows
                id={this.TABLE_ID}
                showHeader
                tableLayout="fixed"
                className="dental-table"
                rowClassName={() => 'editable-row'}
                bordered
                dataSource={elements}
                pagination={false}
                rowKey={(record, index) => this.rowKeyFromId(record.id, index)}
                findIndexForRecordKey={this.findIndexForRecordKey}
                onSortEnd={onDrag}
                scroll={{
                    x: 840,
                }}
            >
                <Column
                    title={this.nameHeaderContent}
                    onHeaderCell={headerStyle}
                    key="dental-examination-row-name"
                    className="dental-examination-row-name"
                    render={this.renderName}
                    fixed={true}
                />
                {!Boolean(group) && (
                    <Column
                        title={this.surfaceHeaderContent}
                        onHeaderCell={headerStyle}
                        key="dental-examination-row-surface"
                        className="dental-examination-row-surface"
                        render={this.renderSurface}
                    />
                )}
                <Column
                    title={this.dateHeaderContent}
                    onHeaderCell={headerStyle}
                    key="dental-examination-row-date"
                    className="dental-examination-row-date"
                    render={this.renderDate}
                />
                <Column
                    title={this.notesHeaderContent}
                    onHeaderCell={headerStyle}
                    key="dental-examination-row-notes"
                    className="dental-examination-row-notes"
                    render={this.renderNotes}
                />
                <Column
                    onHeaderCell={headerStyle}
                    key="dental-examination-row-delete"
                    className={`p-5 dental-examination-row-delete`}
                    fixed="right"
                    render={(_: DentalExamination, el: DentalExamination) => {
                        return {
                            children: (
                                <div className="flex space-x-8 items-center justify-center">
                                    <DeleteOutlined
                                        style={{ color: '#616A8F' }}
                                        size={24}
                                        onClick={() =>
                                            this.props.onElementDelete(el.entity_id || el.id, group)
                                        }
                                        onPointerLeaveCapture={undefined}
                                    />
                                </div>
                            ),
                        };
                    }}
                />
            </TableWithDragableRows>
        );
    }
}

export const DentalExaminationTableComponent = connect()(DentalExaminationTable);

const PadV2DentalExaminationTable: React.FC<{
    teeth: string;
    elements: DentalExamination[];
    surfaces: Surface[];
    onUpdate: () => void;
    chartType: ChartType;
    group?: Group;
}> = ({ teeth, elements, surfaces, onUpdate, chartType, group }) => {
    const dispatch = useDispatch();
    const dentalExaminations = useSelector(getDentalExaminations);

    const onElementSelect = (index?: number, option?: SymDia) => {
        const newData = option || { id: '', name: '' };
        if (group && index !== undefined) {
            const dentalExamination = dentalExaminations[index];
            const examinations = dentalExaminations.filter(
                (i) => i.id === dentalExamination.id && i.group_id === group.id,
            );
            batch(() => {
                examinations.forEach((i) => {
                    const dentalExamination = toPxDentalExaminationFromApiObj(
                        newData,
                        i.teeth_id || '',
                        i.surfaces || [],
                        i.group_id,
                    );
                    dispatch(upsertDentalExamination(dentalExamination, i.index, true));
                });
            });
        } else {
            const dentalExamination = toPxDentalExaminationFromApiObj(newData, teeth, surfaces);
            dispatch(upsertDentalExamination(dentalExamination, index));
        }
    };

    const onElementUpdate = (option: DentalExamination) => {
        option.teeth_id = teeth;
        if (group && option.index !== undefined) {
            const dentalExamination = dentalExaminations[option.index];
            const examinations = dentalExaminations.filter(
                (i) => i.id === dentalExamination.id && i.group_id === group.id,
            );
            batch(() => {
                examinations.forEach((i) => {
                    dispatch(
                        upsertDentalExamination(
                            {
                                ...option,
                                teeth_id: i.teeth_id,
                                surfaces: i.surfaces,
                                group_id: i.group_id,
                            },
                            i.index,
                            true,
                        ),
                    );
                });
            });
        } else {
            dispatch(upsertDentalExamination(option, option.index));
        }
        onUpdate();
    };

    const onElementDelete = (id: string, group?: Group) => {
        const index = dentalExaminations.findIndex((i) => i.entity_id === id);
        dispatch(deleteDentalExamination(id, index, group));
        onUpdate();
    };

    const onCopyDate = (id: string) => {
        const index = dentalExaminations.findIndex((i) => i.entity_id === id);
        const element = dentalExaminations[index];
        if (!element?.since) {
            return;
        }
        dispatch(copyDentalExaminationDate(teeth, element.since, group));
    };

    const onDrag = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): void => {
        if (group) {
            let oI = dentalExaminations.filter((i) => i.group_id === group.id)[oldIndex].index;
            let nI = dentalExaminations.filter((i) => i.group_id === group.id)[newIndex].index;
            if (oI === undefined || nI === undefined) return;
            if (oI !== nI) {
                const teethToSurfacesArray = Object.keys(group.data);
                oI = oI * teethToSurfacesArray.length;
                nI = nI * teethToSurfacesArray.length;
                batch(() => {
                    teethToSurfacesArray.forEach(() => {
                        if (oI !== undefined && nI !== undefined) {
                            oI !== undefined &&
                                nI !== undefined &&
                                dispatch(
                                    swapPxElement(
                                        'dentalExaminations',
                                        Math.max(oI, nI),
                                        Math.min(oI, nI),
                                    ),
                                );
                            oI++;
                            nI++;
                        }
                    });
                });
            }
        } else {
            const oI = elements.filter((i) => i.teeth_id === teeth)[oldIndex].index;
            const nI = elements.filter((i) => i.teeth_id === teeth)[newIndex].index;
            if (oI === undefined || nI === undefined) return;
            if (oI !== nI) {
                dispatch(swapPxElement('dentalExaminations', oI, nI));
            }
        }
    };

    return (
        <DentalExaminationTableComponent
            onElementUpdate={onElementUpdate}
            onElementDelete={onElementDelete}
            onElementSelect={onElementSelect}
            elements={elements}
            containerId={PAD_V2_CONTAINER}
            onDrag={onDrag}
            onCopyDate={onCopyDate}
            chartType={chartType}
            group={group}
        />
    );
};

export default PadV2DentalExaminationTable;

export const RenderCellAutosuggestItemJsx: React.FC<{
    option: { name: string };
    wrapperClassName?: string;
    iconWrapperClassName?: string;
    icon: JSX.Element | string;
}> = ({ option, wrapperClassName, icon, iconWrapperClassName }) => {
    return (
        <div
            className={`flex items-center justify-between ${wrapperClassName} cursor-pointer font-lato w-full`}
        >
            <div className="flex items-center space-x-8">
                <div className={iconWrapperClassName}>
                    <p>{icon}</p>
                </div>
                <p className="text-neutral-1 text-14 w-full">{option.name}</p>
            </div>
        </div>
    );
};
